import { forwardRef } from 'react';
import { ImagePreview } from 'v5/design-system/ImagePreview';
import { cn } from 'v5/platform/dom/cn';
import { TileBox, type TileBoxProps } from './TileBox';

export type DataCardProps = React.ComponentPropsWithRef<'div'> &
  Pick<TileBoxProps, 'still'>;

/**
 * The data-focused body on the shared tile (`TileShell`): a white card with a hairline border, a
 * picture on top (`DataCardImage`) and its data under it (`DataCardHeader`).
 */
export const DataCard = forwardRef<HTMLDivElement, DataCardProps>(
  ({ className, children, ...rest }, ref) => (
    <TileBox
      ref={ref}
      className={cn(
        'group/grid-card h-full flex flex-col bg-bg-raised text-left',
        className
      )}
      {...rest}
    >
      {children}
    </TileBox>
  )
);
DataCard.displayName = 'DataCard';

export interface DataCardImageProps extends React.ComponentPropsWithRef<'div'> {
  src?: string | null;
  alt?: string;
  /** Aspect ratio as string e.g. "4/3" or "1". Default "4/3" */
  aspectRatio?: string;
}

export const DataCardImage = forwardRef<HTMLDivElement, DataCardImageProps>(
  (
    { src, alt = '', aspectRatio = '4/3', className, children, style, ...rest },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(
        'w-full overflow-hidden bg-bg-subtle flex items-center justify-center pointer-events-none',
        className
      )}
      style={{ aspectRatio, ...style }}
      {...rest}
    >
      <ImagePreview
        src={src}
        alt={alt}
        loading="lazy"
        objectFit="cover"
        noUpscale
        className="w-full h-full"
        fallback={
          children ?? (
            <div className="w-full h-full min-h-24 flex items-center justify-center text-fg-muted" />
          )
        }
      />
    </div>
  )
);
DataCardImage.displayName = 'DataCardImage';

export type DataCardHeaderProps = React.ComponentPropsWithRef<'div'>;

/** Row container for title/description and optional actions on the same row. */
export const DataCardHeader = forwardRef<HTMLDivElement, DataCardHeaderProps>(
  ({ className, children, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        'p-1 flex flex-row items-start justify-between gap-2 min-w-0 flex-1',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  )
);
DataCardHeader.displayName = 'DataCardHeader';

export type DataCardHeaderContentProps = React.ComponentPropsWithRef<'div'>;

/** The title and an optional description; the first child of DataCardHeader. */
export const DataCardHeaderContent = forwardRef<
  HTMLDivElement,
  DataCardHeaderContentProps
>(({ className, children, ...rest }, ref) => (
  <div
    ref={ref}
    className={cn('flex p-1.5 flex-col gap-0.5 min-w-0 flex-1', className)}
    {...rest}
  >
    {children}
  </div>
));
DataCardHeaderContent.displayName = 'DataCardHeaderContent';

export type DataCardActionsProps = React.ComponentPropsWithRef<'div'> & {
  /** When true, no top border and compact padding (for same-row layout in header). */
  inline?: boolean;
};

export const DataCardActions = forwardRef<HTMLDivElement, DataCardActionsProps>(
  ({ className, children, inline, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        'shrink-0 flex items-center justify-end gap-1 opacity-0 group-hocus/grid-card:opacity-100 group-data-focus-visible/grid-card:opacity-100 transition-opacity',
        inline ? 'p-0' : 'p-2 border-t border-border-default',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  )
);
DataCardActions.displayName = 'DataCardActions';
